import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Plus, Check, Phone, Search as SearchIcon, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useState } from 'react';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';

interface Task {
  id: string;
  title: string;
  type: 'call' | 'verify' | 'renegotiate';
  status: 'open' | 'in_progress' | 'done';
  notes: string | null;
  due_date: string | null;
  created_at: string;
}

interface ClienteTarefasTabProps {
  customerId: string;
  companyId: string;
}

const typeIcons: Record<string, React.ElementType> = {
  call: Phone,
  verify: SearchIcon,
  renegotiate: RefreshCw
};

const typeLabels: Record<string, string> = {
  call: 'Ligação',
  verify: 'Verificar',
  renegotiate: 'Renegociar'
};

const statusColors: Record<string, string> = {
  open: 'bg-[hsl(var(--basic-100))] text-[hsl(var(--basic-700))]',
  in_progress: 'bg-[hsl(var(--accent-100))] text-[hsl(var(--accent-700))]',
  done: 'bg-muted text-muted-foreground'
};

const statusLabels: Record<string, string> = {
  open: 'Aberta',
  in_progress: 'Em Progresso',
  done: 'Concluída'
};

export function ClienteTarefasTab({ customerId, companyId }: ClienteTarefasTabProps) {
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);
  const [newTask, setNewTask] = useState<{
    title: string;
    type: 'call' | 'verify' | 'renegotiate';
    notes: string;
  }>({
    title: '',
    type: 'call',
    notes: ''
  });

  const { data: tasks = [], isLoading } = useQuery({
    queryKey: ['customer-tasks', customerId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('tasks')
        .select('*')
        .eq('customer_id', customerId)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data as Task[];
    }
  });

  const createMutation = useMutation({
    mutationFn: async () => {
      const { error } = await supabase
        .from('tasks')
        .insert({
          customer_id: customerId,
          company_id: companyId,
          title: newTask.title,
          type: newTask.type,
          notes: newTask.notes || null,
          status: 'open'
        });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customer-tasks', customerId] });
      setIsOpen(false);
      setNewTask({ title: '', type: 'call', notes: '' });
    }
  });

  const completeMutation = useMutation({
    mutationFn: async (taskId: string) => {
      const { error } = await supabase
        .from('tasks')
        .update({ status: 'done', completed_at: new Date().toISOString() })
        .eq('id', taskId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customer-tasks', customerId] });
    }
  });

  const openTasks = tasks.filter(t => t.status !== 'done');
  const doneTasks = tasks.filter(t => t.status === 'done');

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <p className="text-sm text-muted-foreground">
          {openTasks.length} tarefa(s) pendente(s)
        </p>
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
          <DialogTrigger asChild>
            <Button className="gap-2">
              <Plus className="h-4 w-4" />
              Nova Tarefa
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Nova Tarefa</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Título</Label>
                <Input 
                  value={newTask.title}
                  onChange={(e) => setNewTask({...newTask, title: e.target.value})}
                  placeholder="Descreva a tarefa"
                />
              </div>
              <div>
                <Label>Tipo</Label>
                <Select 
                  value={newTask.type}
                  onValueChange={(v) => setNewTask({...newTask, type: v as 'call' | 'verify' | 'renegotiate'})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="call">Ligação</SelectItem>
                    <SelectItem value="verify">Verificar</SelectItem>
                    <SelectItem value="renegotiate">Renegociar</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Observações</Label>
                <Input 
                  value={newTask.notes}
                  onChange={(e) => setNewTask({...newTask, notes: e.target.value})}
                  placeholder="Detalhes adicionais (opcional)"
                />
              </div>
              <Button 
                className="w-full" 
                onClick={() => createMutation.mutate()}
                disabled={!newTask.title || createMutation.isPending}
              >
                Criar Tarefa
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Open Tasks */}
      {isLoading ? (
        <div className="p-8 text-center text-muted-foreground">Carregando...</div>
      ) : openTasks.length === 0 ? (
        <div className="p-8 text-center text-muted-foreground italic bg-muted/30 rounded-lg">
          Nenhuma tarefa pendente.
        </div>
      ) : (
        <div className="bg-card rounded-lg border divide-y">
          {openTasks.map((task) => {
            const Icon = typeIcons[task.type];
            return (
              <div 
                key={task.id} 
                className="flex items-center gap-4 p-4 hover:bg-muted/30 transition-colors"
              >
                <div className="h-10 w-10 rounded-lg bg-[hsl(var(--basic-100))] flex items-center justify-center">
                  <Icon className="h-5 w-5 text-[hsl(var(--basic-600))]" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-[hsl(var(--basic-900))] truncate">{task.title}</p>
                  <p className="text-xs text-muted-foreground">
                    {typeLabels[task.type]} • {new Date(task.created_at).toLocaleDateString('pt-BR')}
                  </p>
                </div>
                <span className={`px-2 py-1 rounded text-xs font-bold uppercase ${statusColors[task.status]}`}>
                  {statusLabels[task.status]}
                </span>
                <Button 
                  variant="ghost" 
                  size="icon"
                  onClick={() => completeMutation.mutate(task.id)}
                  disabled={completeMutation.isPending}
                >
                  <Check className="h-4 w-4 text-[hsl(var(--accent-500))]" />
                </Button>
              </div>
            );
          })}
        </div>
      )}

      {/* Completed Tasks */}
      {doneTasks.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-bold text-muted-foreground uppercase tracking-wider">Concluídas</h4>
          <div className="bg-muted/30 rounded-lg divide-y divide-muted">
            {doneTasks.slice(0, 5).map((task) => {
              const Icon = typeIcons[task.type];
              return (
                <div 
                  key={task.id} 
                  className="flex items-center gap-4 p-4 opacity-60"
                >
                  <div className="h-8 w-8 rounded-lg bg-muted flex items-center justify-center">
                    <Icon className="h-4 w-4 text-muted-foreground" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-muted-foreground line-through truncate">{task.title}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
